"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Song = void 0;
const StringBuilder_1 = require("../../utils/StringBuilder");
const MetadataProperty_1 = require("../common/MetadataProperty");
const Game_1 = require("./lib/Game");
const ScoreMode_1 = require("./lib/ScoreMode");
const Side_1 = require("./lib/Side");
/**
 * Represents data about a TJA chart file.
 */
class Song {
    constructor() {
        /**
         * Song's title that appears on song selection, in the game, and on the
         * results screen.
         */
        this.title = 'untitled';
        /**
         * Localized versions of the title. If a localized title exists, it will be
         * used instead of the `title` property if a user has their game language set
         * to the corresponding language. Supported languages are: Japanese (ja),
         * English (en), Chinese Simplified (cn), Chinese Traditional (tw), and Korean
         * (ko).
         */
        this.localizedTitle = {};
        /**
         * Localized versions of the subtitle. If a localized subtitle exists, it will
         * be used instead of the `subtitle` property if a user has their game
         * language set to the corresponding language. Supported languages are:
         * Japanese (ja), English (en), Chinese Simplified (cn), Chinese Traditional
         * (tw), Korean (ko).
         */
        this.localizedSubtitle = {};
        /**
         * Song's beats per minute. Defaults to 120.
         *
         * The following formula is used: `BPM = MEASURE / SIGN * 4`, where MEASURE is
         * amount of measures per minute and SIGN is the time signature, eg. 4 / 4 if
         * the current time signature is common.
         *
         * @default 120
         */
        this.bpm = 120;
        /**
         * Floating point value for chart offset in seconds. Negative values will
         * delay notes, positive will cause them to appear sooner.
         *
         * @default 0.0
         */
        this.offset = 0.0;
        /**
         * Offset of song preview during song selection in seconds.
         *
         * @default 0.0
         */
        this.demoStart = 0.0;
        /**
         * Scoring method that affects the final score. All scores are divided by 10,
         * rounded towards negative infinity, then multiplied by 10.
         *
         * @default ScoreMode.AC_1_TO_AC_7
         */
        this.scoreMode = ScoreMode_1.ScoreMode.AC_1_TO_AC_7;
        /**
         * Music volume percentage.
         *
         * @default 100
         */
        this.songVolume = 100;
        /**
         * Sound effect volume percentage.
         *
         * @default 100
         */
        this.soundEffectVolume = 100;
        /**
         * Value can be either:
         * - "Normal" or "1"
         * - "Ex" or "2"
         * - "Both" or "3"
         *
         * Value of "Normal" and "1" makes the song appear when song selection is in
         * the default mode. "Ex" and "2" hides the song from default song selection.
         * - The song appears after the user presses the buttons for next song and
         * previous song 20 times alternatingly (10 for each button).
         *
         * Default is "Both", making the song appear during song selection in both
         * modes.
         *
         * @default Side.Both
         */
        this.side = Side_1.Side.Both;
        /**
         * Amount of misses that are allowed to be made before interrupting the game
         * and immediately showing the results screen. Removes the gauge, replacing it
         * with lit up souls that fade one by one after missing a note. The amount is
         * not limited, but only 16 souls fit on screen. Default is 0, which does not
         * limit the misses and will play until the end.
         *
         * @default 0
         */
        this.life = 0;
        /**
         * Value can be either "Taiko" or "Jube". Game will be forced to autoplay mode
         * with "Jube" value. Default is "Taiko".
         *
         * @default Game.Taiko
         */
        this.game = Game_1.Game.Taiko;
        /**
         * Array of courses contained in this TJA chart file.
         */
        this.courses = [];
    }
    /**
     * Adds a course to the song data.
     *
     * @param course - The course object to add.
     */
    addCourse(course) {
        this.courses.push(course);
    }
    /**
     * Converts the song data into TJA format.
     * @returns - A string that will correspond to a TJA file.
     */
    toString() {
        const builder = new StringBuilder_1.StringBuilder()
            .withSeparator('\n')
            .append(`${MetadataProperty_1.SongMetadataProperty.Title}:${this.title}`);
        if (this.localizedTitle.ja) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.TitleJA}:${this.localizedTitle.ja}`);
        }
        if (this.localizedTitle.en) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.TitleEN}:${this.localizedTitle.en}`);
        }
        if (this.localizedTitle.cn) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.TitleCN}:${this.localizedTitle.cn}`);
        }
        if (this.localizedTitle.tw) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.TitleTW}:${this.localizedTitle.tw}`);
        }
        if (this.localizedTitle.ko) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.TitleKO}:${this.localizedTitle.ko}`);
        }
        if (this.subtitle) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.Subtitle}:${this.subtitle}`);
        }
        if (this.localizedSubtitle.ja) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.SubtitleJA}:${this.localizedSubtitle.ja}`);
        }
        if (this.localizedSubtitle.en) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.SubtitleEN}:${this.localizedSubtitle.en}`);
        }
        if (this.localizedSubtitle.cn) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.SubtitleCN}:${this.localizedSubtitle.cn}`);
        }
        if (this.localizedSubtitle.tw) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.SubtitleTW}:${this.localizedSubtitle.tw}`);
        }
        if (this.localizedSubtitle.ko) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.SubtitleKO}:${this.localizedSubtitle.ko}`);
        }
        builder.append(`${MetadataProperty_1.SongMetadataProperty.BPM}:${this.bpm}`);
        if (this.wave) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.Wave}:${this.wave}`);
        }
        builder
            .append(`${MetadataProperty_1.SongMetadataProperty.Offset}:${this.offset}`)
            .append(`${MetadataProperty_1.SongMetadataProperty.DemoStart}:${this.demoStart}`);
        if (this.genre) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.Genre}:${this.genre}`);
        }
        builder.append(`${MetadataProperty_1.SongMetadataProperty.ScoreMode}:${this.scoreMode}`);
        if (this.maker) {
            if (this.maker.url) {
                builder.append(`${MetadataProperty_1.SongMetadataProperty.Maker}:${this.maker.name} <${this.maker.url}>`);
            }
            else {
                builder.append(`${MetadataProperty_1.SongMetadataProperty.Maker}:${this.maker.name}`);
            }
        }
        if (this.lyricsFilePath) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.Lyrics}:${this.lyricsFilePath}`);
        }
        builder
            .append(`${MetadataProperty_1.SongMetadataProperty.SongVol}:${this.songVolume}`)
            .append(`${MetadataProperty_1.SongMetadataProperty.SEVol}:${this.soundEffectVolume}`)
            .append(`${MetadataProperty_1.SongMetadataProperty.Side}:${this.side}`)
            .append(`${MetadataProperty_1.SongMetadataProperty.Life}:${this.life}`)
            .append(`${MetadataProperty_1.SongMetadataProperty.Game}:${this.game}`);
        if (this.headScroll) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.HeadScroll}:${this.headScroll}`);
        }
        if (this.backgroundImagePath) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.BGImage}:${this.backgroundImagePath}`);
        }
        if (this.backgroundMoviePath) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.BGMovie}:${this.backgroundMoviePath}`);
        }
        if (this.movieOffset) {
            builder.append(`${MetadataProperty_1.SongMetadataProperty.MovieOffset}:${this.movieOffset}`);
        }
        builder.append('\n');
        this.courses.forEach((course) => {
            builder.append(course.toString());
        });
        builder.append('\n');
        return builder.toString();
    }
}
exports.Song = Song;
